import {
  Box,
  Button,
  Column,
  ConfirmationDialog,
  Drawer,
  DrawerBody,
  DrawerHeader,
  EditableHeading,
  EyeIcon,
  FormField,
  Paragraph,
  Row,
  Select,
  Spinner,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  useDisclosure,
  useToast,
  TextWithTooltip,
} from "@hightouch/htui";
import pluralize from "pluralize";
import { useEffect, useMemo, useRef, useState } from "react";
import { Controller } from "react-hook-form";
import { useQueryClient } from "react-query";

import { AccordionSection } from "src/components/accordion-section";
import { Editor } from "src/components/editor";
import { Form, useHightouchForm } from "src/components/form";
import { FormBar } from "src/components/form/form-bar";
import { IntegrationIcon } from "src/components/integrations/integration-icon";
import {
  NewPage,
  PageCardContent,
  PageCardHeader,
} from "src/components/layout/new-page";
import { PageSpinner } from "src/components/loading";
import {
  AIDRole,
  useAIDPermissions,
} from "src/components/permission/use-aid-permissions";
import { PermissionedButton } from "src/components/permission";

import {
  useDecisionEngineFeatureMatrixConsentQuery,
  useDecisionEngineFeatureMatrixQuery,
  useFeatureMatrixRelationshipPathsQuery,
  useGetFeatureMatrixColumnsForSegmentQuery,
  useGetFeatureMatrixSqlQuery,
  useDecisionEngineRevocationsModelsQuery,
  useMinimalDecisionEngineFlowsQuery,
  usePreviewFeatureMatrixForFlowQuery,
  useRunSqlResultQuery,
  useSaveDecisionEngineFeatureMatrixMutation,
  useUpdateDecisionEngineFeatureMatrixConsentPurgeMutation,
  useSourceQuery,
} from "src/graphql";
import {
  getColumnTypeIcon,
  getColumnTypeLabel,
} from "src/pages/schema/columns/columns-utils";
import { Results } from "src/components/explore/results";
import type { RoleResolution } from "src/components/explore/role-resolution-chip";
import { ParentIcon } from "src/pages/schema/icons";
import { useDecisionEngineContext } from "src/pages/decision-engines/decision-engine-context";
import { isEqual } from "lodash";
import { useParams } from "src/router";
import { ColumnTable } from "./components/column-table";
import { ConsentHistoryAlert } from "./components/consent-history-alert";
import { ConsentSettings } from "./components/consent-settings";
import { ModelSidebar } from "./components/model-sidebar";
import type {
  ColumnsResult,
  FMColumnConfig,
  FeatureMatrixData,
  FeatureMatrixFormValues,
} from "./types";
import { MAX_INSIGHTS_COLUMNS } from "@hightouch/lib/customer-data/decision-engine/types";
import {
  DEFAULT_CONSENT_PURGE_MODE,
  isConsentPurgeSupportedSource,
  validateConsentPurge,
  type ConsentPurgeValidationError,
} from "@hightouch/lib/customer-data/decision-engine/consent-purge";
import {
  OVERALL_COUNT_KEY,
  type HiddenAggregationColumn,
  type RemovedMergedColumn,
  buildInitialValues,
  buildRelationshipUpserts,
  countInsightsColumns,
  findClearedHiddenAggregations,
  findColumnsMissingAggregations,
  findRemovedMergedColumns,
  readConsentPurge,
  toConsentPurgeInput,
} from "./utils";
import { ensureRowAffected } from "src/utils/hasura-write-guard";
import { canViewFeatureMatrix } from "src/pages/decision-engines/flows/flow-utils";
import { useIsHightouchUser } from "src/utils/is-hightouch-user";

export function FeatureMatrixDetail() {
  const { engine } = useDecisionEngineContext();
  const { featureMatrixId } = useParams<{ featureMatrixId: string }>();
  const isHightouchUser = useIsHightouchUser();

  const fmQuery = useDecisionEngineFeatureMatrixQuery(
    { id: featureMatrixId ?? "" },
    {
      enabled: Boolean(featureMatrixId),
      select: (data) => data.decision_engine_feature_matrices_by_pk,
    },
  );

  const columnsQuery = useGetFeatureMatrixColumnsForSegmentQuery(
    {
      decisionEngineId: engine.id,
      segmentId: Number(fmQuery.data?.parent_model?.id),
    },
    {
      enabled: Boolean(fmQuery.data?.parent_model?.id),
      select: (data) => data.getFeatureMatrixColumnsForSegment,
    },
  );

  if (fmQuery.isLoading || columnsQuery.isLoading) {
    return <PageSpinner />;
  }

  if (!fmQuery.data || !canViewFeatureMatrix(fmQuery.data, isHightouchUser)) {
    return (
      <Column p={6}>
        <Text>Feature matrix not found.</Text>
      </Column>
    );
  }

  if (!fmQuery.data.parent_model) {
    return (
      <Column p={6} gap={2}>
        <Text fontWeight="medium">{fmQuery.data.name}</Text>
        <Text color="text.secondary">
          The parent model for this feature matrix has been deleted.
        </Text>
      </Column>
    );
  }

  if (!columnsQuery.data) {
    return (
      <Column p={6}>
        <Text>Unable to load columns for this feature matrix.</Text>
      </Column>
    );
  }

  return (
    <FeatureMatrixDetailInner
      fm={fmQuery.data}
      columnsResult={columnsQuery.data}
    />
  );
}

function FeatureMatrixDetailInner({
  fm,
  columnsResult,
}: {
  fm: FeatureMatrixData;
  columnsResult: ColumnsResult;
}) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { isPermitted } = useAIDPermissions({
    minRequiredRole: AIDRole.AidAdmin,
  });
  const [selectedModelKey, setSelectedModelKey] = useState<string>("parent");
  const {
    isOpen: previewOpen,
    onOpen: onPreviewOpen,
    onClose: onPreviewClose,
  } = useDisclosure();
  const {
    isOpen: noInsightsWarningOpen,
    onOpen: onNoInsightsWarningOpen,
    onClose: onNoInsightsWarningClose,
  } = useDisclosure();
  const skipNoInsightsWarning = useRef(false);
  const {
    isOpen: mergedRemovalWarningOpen,
    onOpen: onMergedRemovalWarningOpen,
    onClose: onMergedRemovalWarningClose,
  } = useDisclosure();
  const skipMergedRemovalWarning = useRef(false);
  const mergedRemovalConfirmed = useRef(false);
  const [removedMergedColumns, setRemovedMergedColumns] = useState<
    RemovedMergedColumn[]
  >([]);
  const {
    isOpen: clearedAggregationsWarningOpen,
    onOpen: onClearedAggregationsWarningOpen,
    onClose: onClearedAggregationsWarningClose,
  } = useDisclosure();
  const skipClearedAggregationsWarning = useRef(false);
  const clearedAggregationsConfirmed = useRef(false);
  const [clearedAggregationColumns, setClearedAggregationColumns] = useState<
    HiddenAggregationColumn[]
  >([]);
  const [consentErrors, setConsentErrors] = useState<
    ConsentPurgeValidationError[]
  >([]);

  const engineSourceId = fm.parent_model?.connection?.id
    ? String(fm.parent_model.connection.id)
    : null;
  const sourceTypeQuery = useSourceQuery(
    { id: engineSourceId ?? "" },
    {
      enabled: Boolean(engineSourceId),
      select: (data) => data.connections_by_pk?.type,
    },
  );
  const consentPurgeEnabled = isConsentPurgeSupportedSource(
    sourceTypeQuery.data,
  );

  const saveMutation = useSaveDecisionEngineFeatureMatrixMutation({
    onSuccess: () => {},
  });
  const consentPurgeMutation =
    useUpdateDecisionEngineFeatureMatrixConsentPurgeMutation({
      onSuccess: () => {},
    });

  // Its own query so it runs only for sources that support consent purge.
  const consentQuery = useDecisionEngineFeatureMatrixConsentQuery(
    { id: fm.id },
    {
      enabled: consentPurgeEnabled,
      select: (data) => data.decision_engine_feature_matrices_by_pk,
    },
  );
  const matrixConfig = consentQuery.data?.config ?? null;

  const revocationsModelsQuery = useDecisionEngineRevocationsModelsQuery(
    { sourceId: engineSourceId ?? "" },
    {
      enabled: Boolean(consentPurgeEnabled && engineSourceId),
      select: (data) => data.segments,
    },
  );

  const initialValues = useMemo(
    () => buildInitialValues(fm, columnsResult, matrixConfig),
    [fm, columnsResult, matrixConfig],
  );

  const duplicateRelIds = useMemo(() => {
    const groups = new Map<string, string[]>();
    for (const rel of columnsResult.relationships) {
      const list = groups.get(rel.targetModelId) ?? [];
      list.push(rel.relationshipId);
      groups.set(rel.targetModelId, list);
    }
    return [...groups.values()].filter((g) => g.length > 1).flat();
  }, [columnsResult]);

  const pathsQuery = useFeatureMatrixRelationshipPathsQuery(
    { ids: duplicateRelIds },
    { enabled: duplicateRelIds.length > 0 },
  );

  const form = useHightouchForm<FeatureMatrixFormValues>({
    success: false,
    values: initialValues,
    onSubmit: async (data) => {
      const removedMerged = findRemovedMergedColumns(
        initialValues.relationships,
        data.relationships,
        columnsResult,
      );
      if (removedMerged.length > 0 && !skipMergedRemovalWarning.current) {
        setRemovedMergedColumns(removedMerged);
        onMergedRemovalWarningOpen();
        return;
      }

      const clearedAggregations = findClearedHiddenAggregations(
        initialValues.relationships,
        data.relationships,
        columnsResult,
      );
      if (
        clearedAggregations.length > 0 &&
        !skipClearedAggregationsWarning.current
      ) {
        setClearedAggregationColumns(clearedAggregations);
        onClearedAggregationsWarningOpen();
        return;
      }

      const insightsCount = countInsightsColumns(data.relationships);
      if (insightsCount > MAX_INSIGHTS_COLUMNS) {
        toast({
          id: "insights-limit-error",
          title: "Too many insight columns",
          message: `You have ${insightsCount} columns selected for insights (max ${MAX_INSIGHTS_COLUMNS}). Select only the most likely fields you want personalization breakdowns for to reduce noise.`,
          variant: "error",
        });
        return;
      }

      if (insightsCount === 0 && !skipNoInsightsWarning.current) {
        onNoInsightsWarningOpen();
        return;
      }
      skipNoInsightsWarning.current = false;
      // Resetting earlier makes the two dialogs re-trigger each other, later
      // lets a failed save latch the skip.
      skipMergedRemovalWarning.current = false;
      skipClearedAggregationsWarning.current = false;

      const missingAggregations = findColumnsMissingAggregations(
        data.relationships,
        columnsResult,
      );
      if (missingAggregations.length > 0) {
        const missingByModel: Record<string, string[]> = {};
        for (const missing of missingAggregations) {
          if (!missingByModel[missing.modelName]) {
            missingByModel[missing.modelName] = [];
          }
          missingByModel[missing.modelName]!.push(missing.columnName);
          form.setError(
            `relationships.${missing.relationshipKey}.${missing.columnName}.aggregates`,
            { type: "required" },
          );
        }
        const details = Object.entries(missingByModel)
          .map(([model, cols]) => `${model}: ${cols.join(", ")}`)
          .join("; ");
        toast({
          id: "validation-error",
          title: "Missing aggregations",
          message: `The following columns require at least one aggregation — ${details}`,
          variant: "error",
        });
        return;
      }

      const savedConsentPurge = readConsentPurge(matrixConfig);
      const consentPurgeChanged =
        consentPurgeEnabled && !isEqual(savedConsentPurge, data.consentPurge);

      // Only pre-flight consent when the user actually touched it. Otherwise a
      // matrix whose model is missing from the list would be unsavable, and a
      // user without permission to edit consent could never unblock it.
      if (consentPurgeChanged) {
        if (data.consentPurge && !revocationsModelsQuery.isSuccess) {
          toast({
            id: "consent-validation-error",
            title: "Couldn't save changes",
            message:
              "Models for this source haven't loaded yet. Reload the page and try again.",
            variant: "error",
          });
          return;
        }

        const revocationsModel = revocationsModelsQuery.data?.find(
          (model) => String(model.id) === data.consentPurge?.revocationsModelId,
        );
        const validationErrors = validateConsentPurge({
          consentPurge: toConsentPurgeInput(data.consentPurge),
          revocationsModel: revocationsModel
            ? {
                name: revocationsModel.name,
                sourceId: String(revocationsModel.connection_id),
                columns: revocationsModel.columns.map((column) => ({
                  name: column.name,
                  type: column.type ?? "",
                })),
              }
            : null,
          engineSourceId,
        });
        setConsentErrors(validationErrors);
        if (validationErrors.length > 0) {
          toast({
            id: "consent-validation-error",
            title: "Couldn't save changes",
            message: "Fix the error in consent settings and try again.",
            variant: "error",
          });
          return;
        }

        // Consent purge is written first, through its own validating resolver
        // (the config column is not user-updatable through Hasura), so a
        // server rejection leaves the rest of the matrix unsaved rather than
        // half-saved.
        const result = await consentPurgeMutation.mutateAsync({
          featureMatrixId: fm.id,
          consentPurge: toConsentPurgeInput(data.consentPurge),
        });
        const consentResult =
          result.updateDecisionEngineFeatureMatrixConsentPurge;
        if (consentResult.__typename === "ConsentPurgeValidationFailed") {
          setConsentErrors(
            consentResult.errors.map((error) => ({
              field: error.field as ConsentPurgeValidationError["field"],
              message: error.message,
            })),
          );
          toast({
            id: "consent-validation-error",
            title: "Couldn't save changes",
            message: "Fix the error in consent settings and try again.",
            variant: "error",
          });
          return;
        }
      }

      const { relationshipsToUpsert, idsToDelete } =
        await buildRelationshipUpserts({
          featureMatrixId: fm.id,
          data,
          columnsResult,
          savedRelationships: fm.relationships,
        });

      const result = await saveMutation.mutateAsync({
        id: fm.id,
        set: { name: data.name },
        relationships: relationshipsToUpsert,
        deleteRelationshipIds: idsToDelete,
      });
      // One grant gates the feature matrix and its relationships, so the
      // by_pk result is the denial signal for the whole save.
      ensureRowAffected(
        result.update_decision_engine_feature_matrices_by_pk,
        "update this feature matrix",
      );

      toast({
        id: "save-feature-matrix",
        title: "Feature matrix saved",
        variant: "success",
      });

      await queryClient.invalidateQueries(
        useDecisionEngineFeatureMatrixQuery.getKey({ id: fm.id }),
      );
      if (consentPurgeEnabled) {
        await queryClient.invalidateQueries(
          useDecisionEngineFeatureMatrixConsentQuery.getKey({ id: fm.id }),
        );
      }
    },
  });

  const insightsWarningShown = useRef(false);
  useEffect(() => {
    const subscription = form.watch((data) => {
      const count = countInsightsColumns(
        (data.relationships ?? {}) as Record<
          string,
          Record<string, FMColumnConfig>
        >,
      );
      if (count > MAX_INSIGHTS_COLUMNS && !insightsWarningShown.current) {
        insightsWarningShown.current = true;
        toast({
          id: "insights-limit-warning",
          title: "Too many insight columns selected",
          message: `You have ${count} columns selected for insights (max ${MAX_INSIGHTS_COLUMNS}). Select only the most likely fields you want personalization breakdowns for to reduce noise in the plots.`,
          variant: "warning",
        });
      } else if (count <= MAX_INSIGHTS_COLUMNS) {
        insightsWarningShown.current = false;
      }
    });
    return () => subscription.unsubscribe();
  }, [form, toast]);

  return (
    <Form form={form}>
      <NewPage
        title={fm.name}
        contain
        crumbs={[
          {
            label: "Feature matrices",
            link: "/ai/configuration/feature-matrices",
          },
          { label: fm.name },
        ]}
      >
        <PageCardHeader removeBoxShadow hasBorder>
          <Column flex={1} gap={1}>
            <Row justify="space-between" align="center">
              <Controller
                name="name"
                render={({ field }) => (
                  <EditableHeading isDisabled={!isPermitted} {...field} />
                )}
              />
              <Button size="sm" variant="secondary" onClick={onPreviewOpen}>
                Preview
              </Button>
            </Row>
            {fm.parent_model && (
              <Row gap={4} align="center" flexWrap="wrap">
                {fm.parent_model.connection && (
                  <Row gap={1.5} align="center">
                    <IntegrationIcon
                      name={fm.parent_model.connection.definition.name}
                      src={fm.parent_model.connection.definition.icon}
                      size={4}
                    />
                    <TextWithTooltip
                      size="sm"
                      color="text.secondary"
                      sx={{ maxWidth: "3xs" }}
                    >
                      {fm.parent_model.connection.name}
                    </TextWithTooltip>
                  </Row>
                )}
                <Row gap={1.5} align="center">
                  <ParentIcon size="sm" />
                  <TextWithTooltip
                    size="sm"
                    color="text.secondary"
                    sx={{ maxWidth: "3xs" }}
                  >
                    {fm.parent_model.name}
                  </TextWithTooltip>
                </Row>
              </Row>
            )}
          </Column>
        </PageCardHeader>

        <PageCardContent flush overflow="hidden" pos="relative">
          {consentPurgeEnabled && (
            <ConsentSettings
              sourceId={engineSourceId ?? undefined}
              savedMode={
                readConsentPurge(matrixConfig)?.mode ??
                DEFAULT_CONSENT_PURGE_MODE
              }
              errors={consentErrors}
              onClearErrors={(fields) =>
                setConsentErrors((current) =>
                  current.filter((error) => !fields.includes(error.field)),
                )
              }
            />
          )}
          <Row flex={1} minHeight={0}>
            <ModelSidebar
              columnsResult={columnsResult}
              selectedModelKey={selectedModelKey}
              onSelectModel={setSelectedModelKey}
              pathsData={pathsQuery.data}
              parentModelId={String(columnsResult.parentModel.modelId)}
            />
            <Column flex={1} minWidth={0}>
              <ColumnTable
                key={selectedModelKey}
                modelKey={selectedModelKey}
                columnsResult={columnsResult}
                consentPurgeEnabled={consentPurgeEnabled}
                sourceId={engineSourceId ?? undefined}
              />
            </Column>
          </Row>
          <Box
            position="absolute"
            bottom={0}
            left={0}
            right={0}
            pointerEvents="none"
            zIndex={3}
            sx={{ "& > * > *": { pointerEvents: "auto" } }}
          >
            {consentPurgeEnabled && (
              <ConsentHistoryAlert
                savedRelationships={initialValues.relationships}
              />
            )}
            <FormBar />
          </Box>
        </PageCardContent>
      </NewPage>

      <ConfirmationDialog
        isOpen={mergedRemovalWarningOpen}
        title="Remove features from a merged model"
        confirmButtonText="Remove and save"
        variant="danger"
        onClose={() => {
          onMergedRemovalWarningClose();
          // Cancel restores the removed selections. Submitting already cleared
          // the dirty state and the read-only table blocks re-selecting them.
          // The dialog also calls onClose after a confirm, so the confirmed
          // ref gates the restore rather than the skip ref, which onSubmit
          // resets mid-save.
          if (mergedRemovalConfirmed.current) {
            mergedRemovalConfirmed.current = false;
            return;
          }
          for (const removed of removedMergedColumns) {
            const config =
              initialValues.relationships[removed.relationshipKey]?.[
                removed.columnName
              ];
            if (config) {
              form.setValue(
                `relationships.${removed.relationshipKey}.${removed.columnName}`,
                config,
                { shouldDirty: true },
              );
            }
          }
        }}
        onConfirm={async () => {
          mergedRemovalConfirmed.current = true;
          skipMergedRemovalWarning.current = true;
          onMergedRemovalWarningClose();
          await form.submit();
        }}
      >
        <Paragraph>
          You are removing{" "}
          {pluralize("column", removedMergedColumns.length, true)} (
          {removedMergedColumns
            .slice(0, 5)
            .map((c) =>
              c.columnName === OVERALL_COUNT_KEY
                ? "overall count"
                : c.columnName,
            )
            .join(", ")}
          {removedMergedColumns.length > 5 ? ", …" : ""}) from{" "}
          {[...new Set(removedMergedColumns.map((c) => c.modelName))].join(
            ", ",
          )}
          . A removed column can't be re-added here under the same feature name.
          Reselecting it under the model its columns merged into creates a new
          feature, and the agent's learnings for the removed feature won't carry
          over.
        </Paragraph>
      </ConfirmationDialog>

      <ConfirmationDialog
        isOpen={clearedAggregationsWarningOpen}
        title="Remove aggregations"
        confirmButtonText="Remove and save"
        variant="danger"
        onClose={() => {
          onClearedAggregationsWarningClose();
          // The 1:1 table has no aggregation picker, so cancel restores the saved aggregations.
          if (clearedAggregationsConfirmed.current) {
            clearedAggregationsConfirmed.current = false;
            return;
          }
          for (const cleared of clearedAggregationColumns) {
            const path =
              `relationships.${cleared.relationshipKey}.${cleared.columnName}` as const;
            const initial =
              initialValues.relationships[cleared.relationshipKey]?.[
                cleared.columnName
              ];
            const current = form.getValues(path);
            if (initial && current) {
              form.setValue(
                path,
                {
                  ...current,
                  aggregates: initial.aggregates,
                  filters: initial.filters,
                  includeUnfiltered: initial.includeUnfiltered,
                  timeWindowDays: initial.timeWindowDays,
                  insightsExcludedVariants: initial.insightsExcludedVariants,
                },
                { shouldDirty: true },
              );
            }
          }
        }}
        onConfirm={async () => {
          clearedAggregationsConfirmed.current = true;
          skipClearedAggregationsWarning.current = true;
          onClearedAggregationsWarningClose();
          await form.submit();
        }}
      >
        <Paragraph>
          You are removing aggregations from{" "}
          {pluralize("column", clearedAggregationColumns.length, true)} (
          {clearedAggregationColumns
            .slice(0, 5)
            .map((c) => c.columnName)
            .join(", ")}
          {clearedAggregationColumns.length > 5 ? ", …" : ""}) in{" "}
          {[...new Set(clearedAggregationColumns.map((c) => c.modelName))].join(
            ", ",
          )}
          . Their features get new names, and the agent's learnings for the old
          features won't carry over. This can't be undone.
        </Paragraph>
      </ConfirmationDialog>

      <ConfirmationDialog
        isOpen={noInsightsWarningOpen}
        title="No insight features selected"
        confirmButtonText="Save anyway"
        variant="warning"
        onClose={onNoInsightsWarningClose}
        onConfirm={async () => {
          onNoInsightsWarningClose();
          skipNoInsightsWarning.current = true;
          await form.submit();
        }}
      >
        <Paragraph>
          No features are opted in to insights. Without insight features, AI
          Decisioning won't be able to generate performance breakdowns that
          explain why certain recommendations work.
        </Paragraph>
      </ConfirmationDialog>

      {previewOpen && (
        <FeatureMatrixPreviewDrawer
          onClose={onPreviewClose}
          featureMatrixId={fm.id}
          featureMatrixName={fm.name}
          parentModelId={fm.parent_model?.id}
        />
      )}
    </Form>
  );
}

function FeatureMatrixPreviewDrawer({
  onClose,
  featureMatrixId,
  featureMatrixName,
  parentModelId,
}: {
  onClose: () => void;
  featureMatrixId: string;
  featureMatrixName: string;
  parentModelId: string | undefined;
}) {
  const { engine } = useDecisionEngineContext();

  const featureMatrixSqlQuery = useGetFeatureMatrixSqlQuery({
    featureMatrixId,
  });

  const [selectedFlowId, setSelectedFlowId] = useState<string | undefined>();
  const [shouldPoll, setShouldPoll] = useState(false);
  const [queryResult, setQueryResult] = useState<{
    rows: Record<string, unknown>[];
    columns: { name: string; type: string }[];
    roleResolution?: RoleResolution | null;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isRunning, setIsRunning] = useState(false);

  const flowsQuery = useMinimalDecisionEngineFlowsQuery(
    { id: engine.id },
    { select: (data) => data.decision_engine_flows },
  );

  const sqlQuery = usePreviewFeatureMatrixForFlowQuery(
    {
      input: {
        featureMatrixId,
        flowId: selectedFlowId!,
        runSql: false,
      },
    },
    {
      enabled: Boolean(selectedFlowId),
      select: (data) => data.previewFeatureMatrixForFlow,
    },
  );

  const runPreviewQuery = usePreviewFeatureMatrixForFlowQuery(
    {
      input: {
        featureMatrixId,
        flowId: selectedFlowId!,
        runSql: true,
      },
    },
    {
      enabled: false,
    },
  );

  useRunSqlResultQuery(
    {
      jobId: String(runPreviewQuery.data?.previewFeatureMatrixForFlow?.jobId),
      page: 0,
    },
    {
      enabled: Boolean(
        runPreviewQuery.data?.previewFeatureMatrixForFlow?.jobId,
      ),
      refetchInterval: shouldPoll ? 1000 : false,
      onSuccess: (data) => {
        if (!data.backgroundPreviewQueryResult) {
          return;
        }

        if (
          data.backgroundPreviewQueryResult.__typename === "FailedQueryResponse"
        ) {
          setError(data.backgroundPreviewQueryResult.error);
        } else {
          setQueryResult({
            rows: data.backgroundPreviewQueryResult.rows,
            columns: data.backgroundPreviewQueryResult.columns,
            roleResolution: data.backgroundPreviewQueryResult.roleResolution,
          });
        }

        setShouldPoll(false);
        setIsRunning(false);
      },
    },
  );

  const handleFlowChange = (flowId: string | undefined) => {
    setSelectedFlowId(flowId);
    setQueryResult(null);
    setError(null);
    setShouldPoll(false);
    setIsRunning(false);
  };

  const handlePreview = async () => {
    setQueryResult(null);
    setError(null);
    setIsRunning(true);
    try {
      const result = await runPreviewQuery.refetch();
      if (result.data?.previewFeatureMatrixForFlow?.jobId) {
        setShouldPoll(true);
      } else {
        setIsRunning(false);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setIsRunning(false);
    }
  };

  const fmSqlData = featureMatrixSqlQuery.data?.getFeatureMatrixSql;

  const groupedColumns = useMemo(() => {
    const columns = fmSqlData?.columns ?? [];
    const groups = new Map<string, { name: string; type: string }[]>();
    for (const col of columns) {
      const modelName = col.modelName ?? "Other";
      const list = groups.get(modelName) ?? [];
      list.push({ name: col.name, type: col.type });
      groups.set(modelName, list);
    }
    return Array.from(groups.entries()).map(([modelName, cols]) => ({
      modelName,
      columns: cols,
    }));
  }, [fmSqlData?.columns]);

  return (
    <Drawer isOpen onClose={onClose} size="xl">
      <DrawerHeader>{featureMatrixName}</DrawerHeader>
      <DrawerBody>
        <Tabs>
          <TabList>
            <Tab>Features</Tab>
            <Tab>SQL</Tab>
          </TabList>
          <TabPanels>
            <TabPanel>
              {featureMatrixSqlQuery.isLoading ? (
                <Spinner />
              ) : featureMatrixSqlQuery.error ? (
                <Text color="text.danger">
                  Error loading SQL: {String(featureMatrixSqlQuery.error)}
                </Text>
              ) : fmSqlData?.columns && fmSqlData.columns.length > 0 ? (
                <Column>
                  <Row
                    justify="space-between"
                    px={4}
                    py={2}
                    borderBottom="1px solid"
                    borderColor="base.border"
                  >
                    <Text size="sm" color="text.secondary" fontWeight="medium">
                      Source column
                    </Text>
                    <Text size="sm" color="text.secondary" fontWeight="medium">
                      Type
                    </Text>
                  </Row>
                  {groupedColumns.map((group) => (
                    <AccordionSection
                      key={group.modelName}
                      label={group.modelName}
                      count={group.columns.length}
                    >
                      <Column>
                        {group.columns.map((col) => (
                          <Row
                            key={col.name}
                            justify="space-between"
                            align="center"
                            px={4}
                            py={3}
                            borderBottom="1px solid"
                            borderColor="base.border"
                          >
                            <Text>{col.name}</Text>
                            <Row align="center" gap={2}>
                              <Box
                                as={getColumnTypeIcon(col.type)}
                                color="text.secondary"
                                fontSize="lg"
                              />
                              <Text color="text.secondary">
                                {getColumnTypeLabel(col.type)}
                              </Text>
                            </Row>
                          </Row>
                        ))}
                      </Column>
                    </AccordionSection>
                  ))}
                </Column>
              ) : (
                <Text color="text.secondary">No features generated.</Text>
              )}
            </TabPanel>
            <TabPanel>
              <Column gap={6} flex={1} minHeight={0}>
                <FormField
                  label="Agent audience"
                  description={`Preview the SQL that the "${featureMatrixName}" feature matrix generates against any agent's audience, even if this feature matrix isn't attached to that agent. Only the audience is used and the agent's own feature matrix is ignored.`}
                >
                  <Select
                    placeholder="Select an agent"
                    isLoading={flowsQuery.isLoading}
                    options={
                      flowsQuery.data?.map((flow) => ({
                        label: flow.name,
                        value: flow.id,
                      })) ?? []
                    }
                    value={selectedFlowId}
                    onChange={handleFlowChange}
                  />
                </FormField>

                {sqlQuery.isLoading && <Spinner />}

                {sqlQuery.data?.querySql && (
                  <>
                    <Column maxHeight="40vh">
                      <Editor
                        language="sql"
                        bg="base.background"
                        value={sqlQuery.data.querySql}
                        readOnly
                      />
                    </Column>

                    <Row>
                      <PermissionedButton
                        permission={
                          parentModelId
                            ? {
                                v2: {
                                  resource: "model",
                                  grant: "can_preview",
                                  id: parentModelId,
                                },
                              }
                            : undefined
                        }
                        icon={EyeIcon}
                        isDisabled={isRunning}
                        isLoading={isRunning}
                        onClick={handlePreview}
                      >
                        Preview results
                      </PermissionedButton>
                    </Row>
                  </>
                )}

                <Results
                  columns={queryResult?.columns.map((c) => ({
                    ...c,
                    raw_type: null,
                  }))}
                  rows={queryResult?.rows}
                  error={error ?? undefined}
                  isLoading={isRunning}
                  isResultTruncated={false}
                  isPlaceholder={false}
                  roleResolution={queryResult?.roleResolution}
                  onCancel={() => {
                    setShouldPoll(false);
                    setIsRunning(false);
                  }}
                />
              </Column>
            </TabPanel>
          </TabPanels>
        </Tabs>
      </DrawerBody>
    </Drawer>
  );
}
